import * as React from "react";
import styled, {
  css,
  useTheme
} from "styled-components";
import { useLocation } from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import { IRoute } from "src/pages/Router/routes";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";

export interface MainMenuItemProps {
  route: IRoute;
  isParent?: boolean;
}

/**
 * Main menu item in the navigation
 *
 */
const MainMenuItem: React.ReactFCWithChildren<MainMenuItemProps> = props => {
  const { route } = props;
  const [ _menuState, { clickMenuItem } ] = React.useContext(MenuContext);
  const location = useLocation();
  const theme = useTheme();

  // set the classes based on route state
  const menuItemClasses = React.useMemo(() => {
    const classes: string[] =
    [ "menu-item" ];

    if (location.pathname === route.path || location.pathname === route.loadedPath) {
      classes.push("active");
    }

    if (props.isParent) {
      classes.push("parent");
    }

    if (route.disabled) {
      classes.push("disabled");
    }

    return classes.join(" ");
  }, [
    location.pathname,
    props.isParent,
    route.disabled,
    route.loadedPath,
    route.path
  ]);

  return (
    <MainMenuItemWrapper
      className={menuItemClasses}
      title={route.title || ""}
      onClick={() => {
        if (!route.disabled) {
          route.onClick ? route.onClick() : clickMenuItem(route);
        }
      }}
    >
      {props.isParent ? (
        <Icon
          className="menu-icon"
          icon={Icons.CHEVRONLEFT}
          $fillColor="white"
          size={20}
        />
      ) :
        route.icon && (
          <Icon
            className="menu-icon"
            icon={route.icon}
            $fillColor="white"
            size={20}
          />
        )}

      <Text
        color={theme.overrides?.menu?.textColor ? theme.overrides.menu.textColor : "white"}
        className="menu-item-text"
        weight="light"
      >
        {route.menuTitle || route.title}
      </Text>
    </MainMenuItemWrapper>
  );
};

export default MainMenuItem;

const MainMenuItemWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing.padding.sm} 20px; 
    width: 100%;
    text-transform: capitalize;

    .menu-icon {
      margin-right: ${theme.spacing.margins.sm};
      ${theme.overrides?.menu?.iconColor ? css`
      & {
        fill: ${theme.overrides.menu.iconColor} !important;
      }
        ` : ""}
    }

    &.parent {
      border-top: 1px solid ${theme.overrides?.menu?.footerBackground ? theme.overrides.menu.footerBackground : theme.colors.primaryDark};
      border-bottom: 1px solid ${theme.overrides?.menu?.footerBackground ? theme.overrides.menu.footerBackground : theme.colors.primaryDark};
    }

    &.active {
      .menu-item-text {
        font-weight: ${theme.fonts.weights.bold};
      }
    }

    &:hover {
      cursor: pointer;
      
      .menu-item-text {
        font-weight: ${theme.fonts.weights.bold};
      }

      .menu-icon {
        fill: ${theme.colors.accentLight};
      }
    }
  `}`;
